import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-book-open-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading text-xl font-bold transition-colors ${
              isScrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
            }`}>
              韩漫之家
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? 'bg-primary-100 text-primary-700'
                  : isScrolled || !isHome
                    ? 'text-foreground-700 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              首页
            </Link>

            <div ref={categoryRef} className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                  location.pathname.startsWith('/category')
                    ? 'bg-primary-100 text-primary-700'
                    : isScrolled || !isHome
                      ? 'text-foreground-700 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-xl border border-background-200/70 py-2 w-48 shadow-lg">
                  {categories.filter(c => c.slug !== 'all').map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <div className="w-7 h-7 flex items-center justify-center">
                        <i className={`${cat.icon} text-base text-secondary-500`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? 'bg-primary-100 text-primary-700'
                  : isScrolled || !isHome
                    ? 'text-foreground-700 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画..."
                className={`pl-9 pr-4 py-2 rounded-full text-sm border transition-colors w-48 focus:w-64 focus:outline-none ${
                  isScrolled || !isHome
                    ? 'bg-background-100 border-background-200/70 text-foreground-950 placeholder:text-foreground-400'
                    : 'bg-white/15 border-white/20 text-background-50 placeholder:text-background-50/50'
                }`}
              />
              <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                <i className={`ri-search-line text-sm ${isScrolled || !isHome ? 'text-foreground-400' : 'text-background-50/60'}`}></i>
              </div>
            </div>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
              isScrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/' ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            {categories.filter(c => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`block px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                  location.pathname === `/category/${cat.slug}` ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center inline mr-2">
                  <i className={`${cat.icon} text-sm text-secondary-500`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/about' ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
            <div className="px-4 pt-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="pl-9 pr-4 py-2.5 rounded-full text-sm border bg-background-100 border-background-200/70 text-foreground-950 placeholder:text-foreground-400 w-full focus:outline-none"
                />
                <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                  <i className="ri-search-line text-sm text-foreground-400"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}